// src/pages/team/TeamSettingsPage.tsx

import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';
import { refEntity } from '@/features/team/logic';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

type SettingsTab = 'general' | 'security' | 'appearance' | 'notifications';

interface MembershipForm {
  require_invitation: boolean;
  allow_self_join: boolean;
  require_admin_approval: boolean;
  invitation_expiry_days: number;
  allow_member_invitations: boolean;
  max_invitations_per_user: number;
  welcome_message: string;
}

interface SecurityForm {
  enable_audit_logging: boolean;
  require_2fa_for_admins: boolean;
  enable_ip_restrictions: boolean;
  allowed_ip_ranges: string;
  session_timeout_minutes: number;
  max_concurrent_sessions: number;
}

interface UIForm {
  theme: string;
  language: string;
  timezone: string;
  date_format: string;
  time_format: string;
  compact_mode: boolean;
  sidebar_layout: string;
}

interface NotificationsForm {
  notify_on_member_join: boolean;
  notify_on_member_leave: boolean;
  notify_on_role_change: boolean;
  notify_on_invitation_sent: boolean;
}

// TABS moved inside component to use t()

function ToggleSwitch({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-center justify-between py-3">
      <div>
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  );
}

export const TeamSettingsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t } = useI18n();

  const TABS: { id: SettingsTab; label: string }[] = [
    { id: 'general', label: t('team.settings.general') },
    { id: 'security', label: t('team.settings.security') },
    { id: 'appearance', label: t('team.settings.appearance') },
    { id: 'notifications', label: t('team.settings.notifications') },
  ];

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [submitting, setSubmitting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const [membershipForm, setMembershipForm] = useState<MembershipForm>({
    require_invitation: true,
    allow_self_join: false,
    require_admin_approval: true,
    invitation_expiry_days: 7,
    allow_member_invitations: false,
    max_invitations_per_user: 5,
    welcome_message: '',
  });

  const [securityForm, setSecurityForm] = useState<SecurityForm>({
    enable_audit_logging: true,
    require_2fa_for_admins: false,
    enable_ip_restrictions: false,
    allowed_ip_ranges: '',
    session_timeout_minutes: 60,
    max_concurrent_sessions: 5,
  });

  const [uiForm, setUIForm] = useState<UIForm>({
    theme: 'system',
    language: 'en',
    timezone: 'UTC',
    date_format: 'YYYY-MM-DD',
    time_format: '24h',
    compact_mode: false,
    sidebar_layout: 'auto',
  });

  const [notificationsForm, setNotificationsForm] = useState<NotificationsForm>({
    notify_on_member_join: true,
    notify_on_member_leave: true,
    notify_on_role_change: true,
    notify_on_invitation_sent: true,
  });

  useEffect(() => {
    if (!id) return;
    Promise.all([
      store.GetTeam(id),
      store.GetTeamSecuritySettings(id).catch(() => null),
      store.GetTeamUISettings(id).catch(() => null),
    ]).then(([_, secRes, uiRes]: any[]) => {
      if (secRes) {
        setSecurityForm((prev) => ({ ...prev, ...secRes }));
      }
      if (uiRes) {
        setUIForm((prev) => ({ ...prev, ...uiRes }));
      }
    });
    return () => { store.clearCurrent(); };
  }, [id]);

  useEffect(() => {
    const item = store.currentItem as any;
    if (!item?.settings) return;
    const s = item.settings;
    if (s.membership) setMembershipForm((prev) => ({ ...prev, ...s.membership }));
    if (s.security) setSecurityForm((prev) => ({ ...prev, ...s.security }));
    if (s.ui) setUIForm((prev) => ({ ...prev, ...s.ui }));
    if (s.notifications) setNotificationsForm((prev) => ({ ...prev, ...s.notifications }));
  }, [store.currentItem]);

  const showSuccess = (msg: string) => {
    setSaveSuccess(msg);
    setTimeout(() => setSaveSuccess(null), 3000);
  };

  const handleSaveGeneral = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await store.UpdateTeam(id, { settings: { membership: membershipForm } } as any);
      showSuccess(t('team.settingsSaved'));
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const handleSaveSecurity = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await store.UpdateTeamSecuritySettings(id, securityForm as any);
      showSuccess(t('team.settingsSaved'));
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const handleSaveAppearance = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await store.UpdateTeamUISettings(id, uiForm as any);
      showSuccess(t('team.settingsSaved'));
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const handleSaveNotifications = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await store.UpdateTeam(id, { settings: { notifications: notificationsForm } } as any);
      showSuccess(t('team.settingsSaved'));
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const handleResetSecurity = async () => {
    if (!id || !confirm(t('team.resetConfirm'))) return;
    try {
      await store.ResetTeamSecuritySettings(id, { team: refEntity(id) });
      showSuccess(t('team.settingsReset'));
    } catch { /* store handles */ }
  };

  const handleResetUI = async () => {
    if (!id || !confirm(t('team.resetConfirm'))) return;
    try {
      await store.ResetTeamUISettings(id, { team: refEntity(id) });
      showSuccess(t('team.settingsReset'));
    } catch { /* store handles */ }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');

  if (store.isLoading && !store.currentItem) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('team.settings.membershipTitle')}</CardTitle>
          <CardDescription>{t('team.settings.membershipDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <ToggleSwitch
            checked={membershipForm.require_invitation}
            onChange={(v) => setMembershipForm((p) => ({ ...p, require_invitation: v }))}
            label={t('team.settings.requireInvitation')}
            description={t('team.settings.requireInvitationDesc')}
          />
          <ToggleSwitch
            checked={membershipForm.allow_self_join}
            onChange={(v) => setMembershipForm((p) => ({ ...p, allow_self_join: v }))}
            label={t('team.settings.allowSelfJoin')}
            description={t('team.settings.allowSelfJoinDesc')}
          />
          <ToggleSwitch
            checked={membershipForm.require_admin_approval}
            onChange={(v) => setMembershipForm((p) => ({ ...p, require_admin_approval: v }))}
            label={t('team.settings.requireAdminApproval')}
            description={t('team.settings.requireAdminApprovalDesc')}
          />
          <ToggleSwitch
            checked={membershipForm.allow_member_invitations}
            onChange={(v) => setMembershipForm((p) => ({ ...p, allow_member_invitations: v }))}
            label={t('team.settings.allowMemberInvitations')}
            description={t('team.settings.allowMemberInvitationsDesc')}
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t('team.settings.invitationTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('team.settings.invitationExpiryDays')}
              type="number"
              value={String(membershipForm.invitation_expiry_days)}
              onChange={(e) => setMembershipForm((p) => ({ ...p, invitation_expiry_days: parseInt(e.target.value, 10) || 7 }))}
            />
            <Input
              label={t('team.settings.maxInvitationsPerUser')}
              type="number"
              value={String(membershipForm.max_invitations_per_user)}
              onChange={(e) => setMembershipForm((p) => ({ ...p, max_invitations_per_user: parseInt(e.target.value, 10) || 5 }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">{t('team.settings.welcomeMessage')}</label>
            <textarea
              value={membershipForm.welcome_message}
              onChange={(e) => setMembershipForm((p) => ({ ...p, welcome_message: e.target.value }))}
              placeholder={t('team.settings.welcomeMessagePlaceholder')}
              rows={3}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveGeneral} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('team.settings.securityTitle')}</CardTitle>
          <CardDescription>{t('team.settings.securityDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="divide-y divide-border">
            <ToggleSwitch
              checked={securityForm.enable_audit_logging}
              onChange={(v) => setSecurityForm((p) => ({ ...p, enable_audit_logging: v }))}
              label={t('team.settings.enableAuditLogging')}
              description={t('team.settings.enableAuditLoggingDesc')}
            />
            <ToggleSwitch
              checked={securityForm.require_2fa_for_admins}
              onChange={(v) => setSecurityForm((p) => ({ ...p, require_2fa_for_admins: v }))}
              label={t('team.settings.require2fa')}
              description={t('team.settings.require2faDesc')}
            />
            <ToggleSwitch
              checked={securityForm.enable_ip_restrictions}
              onChange={(v) => setSecurityForm((p) => ({ ...p, enable_ip_restrictions: v }))}
              label={t('team.settings.enableIpRestrictions')}
              description={t('team.settings.enableIpRestrictionsDesc')}
            />
          </div>
          {securityForm.enable_ip_restrictions && (
            <Input
              label={t('team.settings.allowedIpRanges')}
              type="text"
              value={securityForm.allowed_ip_ranges}
              onChange={(e) => setSecurityForm((p) => ({ ...p, allowed_ip_ranges: e.target.value }))}
              placeholder={t('team.settings.allowedIpRangesPlaceholder')}
            />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('team.settings.sessionTimeout')}
              type="number"
              value={String(securityForm.session_timeout_minutes)}
              onChange={(e) => setSecurityForm((p) => ({ ...p, session_timeout_minutes: parseInt(e.target.value, 10) || 60 }))}
            />
            <Input
              label={t('team.settings.maxConcurrentSessions')}
              type="number"
              value={String(securityForm.max_concurrent_sessions)}
              onChange={(e) => setSecurityForm((p) => ({ ...p, max_concurrent_sessions: parseInt(e.target.value, 10) || 5 }))}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="ghost" onClick={handleResetSecurity}>{t('team.settings.resetDefaults')}</Button>
          <Button onClick={handleSaveSecurity} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('team.settings.appearanceTitle')}</CardTitle>
          <CardDescription>{t('team.settings.appearanceDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t('team.settings.theme')}</label>
              <Select
                value={uiForm.theme}
                onChange={(e) => setUIForm((p) => ({ ...p, theme: e.target.value }))} fullWidth={false}>
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </Select>
            </div>
            <Input
              label={t('team.settings.language')}
              type="text"
              value={uiForm.language}
              onChange={(e) => setUIForm((p) => ({ ...p, language: e.target.value }))}
              placeholder="en"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('team.settings.timezone')}
              type="text"
              value={uiForm.timezone}
              onChange={(e) => setUIForm((p) => ({ ...p, timezone: e.target.value }))}
              placeholder="UTC"
            />
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t('team.settings.sidebarLayout')}</label>
              <Select
                value={uiForm.sidebar_layout}
                onChange={(e) => setUIForm((p) => ({ ...p, sidebar_layout: e.target.value }))} fullWidth={false}>
                <option value="auto">Auto</option>
                <option value="expanded">Expanded</option>
                <option value="collapsed">Collapsed</option>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('team.settings.dateFormat')}
              type="text"
              value={uiForm.date_format}
              onChange={(e) => setUIForm((p) => ({ ...p, date_format: e.target.value }))}
              placeholder="YYYY-MM-DD"
            />
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-foreground">{t('team.settings.timeFormat')}</label>
              <Select
                value={uiForm.time_format}
                onChange={(e) => setUIForm((p) => ({ ...p, time_format: e.target.value }))} fullWidth={false}>
                <option value="24h">24-hour</option>
                <option value="12h">12-hour</option>
              </Select>
            </div>
          </div>
          <ToggleSwitch
            checked={uiForm.compact_mode}
            onChange={(v) => setUIForm((p) => ({ ...p, compact_mode: v }))}
            label={t('team.settings.compactMode')}
            description={t('team.settings.compactModeDesc')}
          />
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="ghost" onClick={handleResetUI}>{t('team.settings.resetDefaults')}</Button>
          <Button onClick={handleSaveAppearance} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('team.settings.notificationsTitle')}</CardTitle>
          <CardDescription>{t('team.settings.notificationsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <ToggleSwitch
            checked={notificationsForm.notify_on_member_join}
            onChange={(v) => setNotificationsForm((p) => ({ ...p, notify_on_member_join: v }))}
            label={t('team.settings.notifyMemberJoin')}
            description={t('team.settings.notifyMemberJoinDesc')}
          />
          <ToggleSwitch
            checked={notificationsForm.notify_on_member_leave}
            onChange={(v) => setNotificationsForm((p) => ({ ...p, notify_on_member_leave: v }))}
            label={t('team.settings.notifyMemberLeave')}
            description={t('team.settings.notifyMemberLeaveDesc')}
          />
          <ToggleSwitch
            checked={notificationsForm.notify_on_role_change}
            onChange={(v) => setNotificationsForm((p) => ({ ...p, notify_on_role_change: v }))}
            label={t('team.settings.notifyRoleChange')}
            description={t('team.settings.notifyRoleChangeDesc')}
          />
          <ToggleSwitch
            checked={notificationsForm.notify_on_invitation_sent}
            onChange={(v) => setNotificationsForm((p) => ({ ...p, notify_on_invitation_sent: v }))}
            label={t('team.settings.notifyInvitationSent')}
            description={t('team.settings.notifyInvitationSentDesc')}
          />
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSaveNotifications} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/teams" className="text-muted-foreground hover:text-foreground">
            {t('team.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">
            {teamName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('team.settingsLabel')}</span>
        </nav>
        <TeamSectionNav teamId={id ?? ''} group="settings" active="settings" />

        <h1 className="text-2xl font-bold text-foreground">{teamName} — {t('team.settingsLabel')}</h1>
      </div>

      {saveSuccess && (
        <Alert variant="success" dismissible onDismiss={() => setSaveSuccess(null)}>
          {saveSuccess}
        </Alert>
      )}
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="border-b border-border mb-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'general' && renderGeneralTab()}
      {activeTab === 'security' && renderSecurityTab()}
      {activeTab === 'appearance' && renderAppearanceTab()}
      {activeTab === 'notifications' && renderNotificationsTab()}
    </div>
  );
});

export default TeamSettingsPage;
